import { Box, Card, CardHeader, CardProps, Container, Grid, Stack, Typography, styled, useTheme } from "@mui/material";
import { ApexOptions } from "apexcharts";
import { useCallback, useEffect } from "react";
import Chart, { useChart } from 'src/components/chart';
import Iconify from "src/components/iconify/iconify";
import { useSettingsContext } from "src/components/settings";
import useModelData from "src/hooks/use-model-data";
import axios, { endpoints } from 'src/utils/axios';
import { DashboardData } from "src/models";
import { DisplayPageData } from "src/components/display-page-data";
import { useRouter } from "src/routes/hooks";
import { useAuthContext } from "src/auth/hooks";
import { PATH_AFTER_LOGIN_DEVICE_ONLY, PATH_AFTER_LOGIN_ELD_ONLY, PATH_AFTER_LOGIN_IFTA_ONLY, PATH_AFTER_LOGIN_RENTALS_ONLY, PATH_AFTER_LOGIN_IS_PARENT } from "src/config-global";

interface AppWidgetSummaryProps extends CardProps {
  title: string;
  total: number;
  percent?: number;
  count?: string;
  totalPrefix?: string;
  chart: {
    colors?: string[];
    series: number[];
    options?: ApexOptions;
  };
}

function AppWidgetSummary({ title, percent, count, totalPrefix, total, chart, sx, ...other }: AppWidgetSummaryProps) {
  const theme = useTheme();

  const {
    colors = [theme.palette.primary.light, theme.palette.primary.main],
    series,
    options,
  } = chart;

  const chartOptions = {
    colors: colors.map((colr) => colr[1]),
    fill: {
      type: 'gradient',
      gradient: {
        colorStops: [
          { offset: 0, color: colors[0] },
          { offset: 100, color: colors[1] },
        ],
      },
    },
    chart: {
      sparkline: {
        enabled: true,
      },
    },
    plotOptions: {
      bar: {
        columnWidth: '68%',
        borderRadius: 2,
      },
    },
    tooltip: {
      x: { show: false },
      y: {
        formatter: (value: number) => `${value}`,
        title: {
          formatter: () => '',
        },
      },
      marker: { show: false },
    },
    ...options,
  } as ApexOptions;

  return (
    <Card sx={{ display: 'flex', alignItems: 'center', p: 3, ...sx }} {...other}>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="subtitle2">{title}</Typography>

        <Stack direction="row" alignItems="center" sx={{ mt: 2, mb: 1 }}>
          {percent !== undefined &&
            <>
              <Iconify
                width={24}
                icon={
                  percent < 0
                    ? 'solar:double-alt-arrow-down-bold-duotone'
                    : 'solar:double-alt-arrow-up-bold-duotone'
                }
                sx={{
                  mr: 1,
                  color: 'success.main',
                  ...(percent < 0 && {
                    color: 'error.main',
                  }),
                }}
              />

              <Typography component="div" variant="subtitle2">
                {percent > 0 && '+'}

                {percent}
              </Typography>
            </>
          }

          {count !== undefined &&
            <Typography component="div" variant="subtitle2">
              {count}
            </Typography>
          }
        </Stack>

        <Typography variant="h3">{totalPrefix}{total}</Typography>
      </Box>

      <Chart type="bar" series={[{ data: series }]} options={chartOptions} width={60} height={36} />
    </Card>
  );
}


const CHART_HEIGHT = 400;

const LEGEND_HEIGHT = 72;

const StyledChart = styled(Chart)(({ theme }) => ({
  height: CHART_HEIGHT,
  '& .apexcharts-canvas, .apexcharts-inner, svg, foreignObject': {
    height: `100% !important`,
  },
  '& .apexcharts-legend': {
    height: LEGEND_HEIGHT,
    borderTop: `dashed 1px ${theme.palette.divider}`,
    top: `calc(${CHART_HEIGHT - LEGEND_HEIGHT}px) !important`,
  },
}));



interface AppCurrentDownloadProps extends CardProps {
  title?: string;
  subheader?: string;
  chart: {
    colors?: string[];
    series: {
      label: string;
      value: number;
    }[];
    options?: ApexOptions;
  };
}

function AppCurrentDownload({ title, subheader, chart, ...other }: AppCurrentDownloadProps) {
  const theme = useTheme();

  const { colors, series, options } = chart;

  const chartSeries = series.map((i) => i.value);

  const chartOptions = useChart({
    chart: {
      sparkline: {
        enabled: true,
      },
    },
    colors,
    labels: series.map((i) => i.label),
    stroke: { colors: [theme.palette.background.paper] },
    legend: {
      offsetY: 0,
      floating: true,
      position: 'bottom',
      horizontalAlign: 'center',
    },
    tooltip: {
      fillSeriesColor: false,
      y: {
        formatter: (value: number) => `${value}`,
        title: {
          formatter: (seriesName: string) => `${seriesName}`,
        },
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '90%',
          labels: {
            value: {
              formatter: (value: number | string) => `${value}`,
            },
            total: {
              formatter: (w: { globals: { seriesTotals: number[] } }) => {
                const sum = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                return `${sum}`;
              },
            },
          },
        },
      },
    },
    ...options,
  });

  return (
    <Card {...other}>
      <CardHeader title={title} subheader={subheader} sx={{ mb: 5 }} />

      <StyledChart
        dir="ltr"
        type="donut"
        series={chartSeries}
        options={chartOptions}
        height={280}
      />
    </Card>
  );
}




interface AnalyticsConversionRatesProps extends CardProps {
  title?: string;
  subheader?: string;
  chart: {
    colors?: string[];
    series: {
      label: string;
      value: number;
    }[];
    options?: ApexOptions;
  };
}

function AnalyticsConversionRates({ title, subheader, chart, ...other }: AnalyticsConversionRatesProps) {
  const { colors, series, options } = chart;

  const chartSeries = series.map((i) => i.value);

  const chartOptions = useChart({
    colors,
    tooltip: {
      marker: { show: false },
      y: {
        formatter: (value: number) => `${value}`,
        title: {
          formatter: () => '',
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '28%',
        borderRadius: 2,
      },
    },
    xaxis: {
      categories: series.map((i) => i.label),
    },
    ...options,
  });

  return (
    <Card {...other}>
      <CardHeader title={title} subheader={subheader} />

      <Box sx={{ mx: 3 }}>
        <Chart
          type="bar"
          dir="ltr"
          series={[{ data: chartSeries }]}
          options={chartOptions}
          height={364}
        />
      </Box>
    </Card>
  );
}

type PageModel = {
  dashboardData: DashboardData
}

export default function Page() {
  const settings = useSettingsContext();
  const theme = useTheme();
  const router = useRouter();
  const { logout, session } = useAuthContext();



  if (session?.organization.isParent) {
    router.push(PATH_AFTER_LOGIN_IS_PARENT);
  } else if (session?.organization?.rentalsOnly) {
    router.push(PATH_AFTER_LOGIN_RENTALS_ONLY)
  } else if (session?.organization?.eldOnly) {
    router.push(PATH_AFTER_LOGIN_ELD_ONLY)
  } else if (session?.organization?.iftaOnly) {
    router.push(PATH_AFTER_LOGIN_IFTA_ONLY)
  } else if (session?.organization?.deviceOnly) {
    router.push(PATH_AFTER_LOGIN_DEVICE_ONLY)
  }

  const dataLoader = useCallback(() => ({
    dashboardData: axios.get(endpoints.dashboardData.find).then(response => response.data).catch(error => {
      if (error.response?.status === 403) {
        router.push("/user-inactive")
      } else if (error.response?.status === 401) {
        logout();
        router.push("/auth/login")
      }
    })
  }), [router, logout]);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      alert(error);
    }
  }, [loading, error]);


  const dashboardData = data?.dashboardData;
  return (
    <Container maxWidth={settings.themeStretch ? false : 'xl'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {dashboardData &&
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <AppWidgetSummary
                title="Loads"
                count={`${dashboardData.loadsLast28Days} Last 28 days`}
                total={dashboardData.loadsAmtLast28Days / 100}
                totalPrefix="$"
                chart={{
                  series: dashboardData.loadsAmtPerWeek.map(l => l / 100),
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <AppWidgetSummary
                title="Charges"
                count={`${dashboardData.chargesLast28Days} Last 28 days`}
                total={dashboardData.chargesAmtLast28Days / 100}
                totalPrefix="$"
                chart={{
                  colors: [theme.palette.info.light, theme.palette.info.main],
                  series: dashboardData.chargesAmtPerWeek.map(l => l / 100),
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <AppWidgetSummary
                title="Deductions"
                count={`${dashboardData.deductionsLast28Days} Last 28 days`}
                total={dashboardData.deductionsAmtLast28Days / 100}
                totalPrefix="$"
                chart={{
                  colors: [theme.palette.warning.light, theme.palette.warning.main],
                  series: dashboardData.deductionsAmtPerWeek.map(l => l / 100),
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <AppWidgetSummary
                title="Driver Pay"
                count={`${dashboardData.driversLast28Days} Last 28 days`}
                total={dashboardData.driverPayLast28Days / 100}
                totalPrefix="$"
                chart={{
                  colors: [theme.palette.warning.light, theme.palette.warning.main],
                  series: dashboardData.driversAmtPerWeek.map(l => l / 100),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <AppCurrentDownload
                title="Drivers"
                chart={{
                  series: [
                    { label: 'Company', value: dashboardData.companyDrivers },
                    { label: 'Non-Company', value: dashboardData.nonCompanyDrivers },
                  ],
                }}
              />
            </Grid>

            <Grid item xs={12} lg={8}>
              <AnalyticsConversionRates
                title="Loads by Business partner"
                subheader="Last 28 days"
                chart={{
                  series: dashboardData.amtByBusinessPartner.map(l => ({ label: l.businessPartnerName, value: l.amount / 100 })),
                }}
              />
            </Grid>
          </Grid>
        }
      </DisplayPageData>
    </Container>
  );
}
